import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { PATHS, PAYWALL_PATHS } from '../../../constants';
import { useModal, usePaymentPlanStatus } from '../../../hooks';
import { getFeatureFlags } from '../../../featureFlags';
import { ExcessUsage, PlanExpired } from '.';

const isAllowedPath = (pathname: string) =>
    !PAYWALL_PATHS.some((privatePath: string): boolean =>
        pathname.includes(privatePath),
    );

interface CheckPaymentPlanStatusProps {
    children: ReactNode;
}

/**
 * If the user has a subscription that has expired, or has a payment plan that will not cover their
 * royalty usage, shows the PlanExpired modal in order to block the user from using the main
 * features of the app. They will still be able to use the Settings page to update their plan.
 * @param props - see {@link PaymentPlanStatusProviderProps}
 */
export const CheckPaymentPlanStatus = ({
    children,
}: CheckPaymentPlanStatusProps): JSX.Element => {
    const { add: addModal, hasOpenModalRef } = useModal();
    const { paywall } = getFeatureFlags();
    const { pathname } = useLocation();
    const history = useHistory();
    const currentPathIsAllowed = useMemo(
        () => isAllowedPath(pathname),
        [pathname],
    );
    const { subscriptionSettings, usage } = usePaymentPlanStatus();
    const active = subscriptionSettings?.subscription_active ?? true;
    const planType = subscriptionSettings?.subscription_plan_type ?? null;
    const modalIdPlanExpiredRef = useRef<Maybe<string>>(null);
    const modalIdExcessUsageRef = useRef<Maybe<string>>(null);

    // Add PlanExpired modal if applicable - but do this only if the ExcessUsage modal is not open
    useEffect(() => {
        if (
            paywall &&
            !active &&
            hasOpenModalRef.current === false &&
            !currentPathIsAllowed &&
            modalIdPlanExpiredRef.current === null &&
            modalIdExcessUsageRef.current === null
        ) {
            const nextModalId = addModal({
                Content: PlanExpired,
                input: { planType },
                handleClose: () => {
                    modalIdPlanExpiredRef.current = null;
                },
            });

            modalIdPlanExpiredRef.current = nextModalId;

            history.push({
                search: history.location.search,
                pathname: PATHS.SETTINGS,
                state: {
                    fromLink: true,
                },
            });
        }
    }, [
        active,
        currentPathIsAllowed,
        history.location.search,
        hasOpenModalRef.current,
        modalIdPlanExpiredRef.current,
        modalIdExcessUsageRef.current,
        paywall,
    ]);

    // Add ExcessUsage modal if applicable - but only if the PlanExpired modal is not open
    useEffect(() => {
        if (
            paywall &&
            usage.exceedsCapacity &&
            hasOpenModalRef.current === false &&
            !currentPathIsAllowed &&
            modalIdPlanExpiredRef.current === null &&
            modalIdExcessUsageRef.current === null
        ) {
            const nextModalId = addModal({
                Content: ExcessUsage,
                input: { planType },
                handleClose: () => {
                    modalIdExcessUsageRef.current = null;
                },
            });

            modalIdExcessUsageRef.current = nextModalId;

            history.push({
                search: history.location.search,
                pathname: PATHS.SETTINGS,
                state: {
                    fromLink: true,
                },
            });
        }
    }, [
        currentPathIsAllowed,
        history.location.search,
        usage.exceedsCapacity,
        hasOpenModalRef.current,
        modalIdPlanExpiredRef.current,
        modalIdExcessUsageRef.current,
        paywall,
    ]);

    return <>{children}</>;
};
